import { useState } from "react";
import { getMonthlyInstalment } from "shared/getMonthlyInstalment";
import { LOAN_RATE_DEFAULT } from "web_myplann/Goals/constants";
import type { GoalDetail } from "web_myplann/Goals/Types/GoalDetail";
import { GOAL_CATEGORY_CONFIG } from "web_myplann/Goals/Types/GoalTypes";
import { FuturePortfolioTypeEnum } from "web_myplann/Infrastructure/Api/Api";

type LoanTermConfig = {
	termYears: number;
	termMin: number;
	termMax: number;
};

const MORTGAGE_TERM: LoanTermConfig = {
	termYears: 30,
	termMin: 5,
	termMax: 40,
};

const LOAN_TERM: LoanTermConfig = {
	termYears: 10,
	termMin: 1,
	termMax: 10,
};

type LoanTotals = {
	monthlyPayment: number;
	totalPaid: number;
	overpay: number;
};

const computeLoanTotals = (
	loanAmount: number,
	annualRatePercent: number,
	termYears: number,
): LoanTotals => {
	const n = termYears * 12;
	if (n <= 0) {
		return { monthlyPayment: 0, totalPaid: 0, overpay: 0 };
	}
	const monthlyPayment = getMonthlyInstalment(
		annualRatePercent,
		termYears,
		loanAmount,
	);
	const totalPaid = monthlyPayment * n;
	const overpay = Math.max(0, totalPaid - loanAmount);
	return { monthlyPayment, totalPaid, overpay };
};

type UseLoanCalculatorResult = {
	isMortgage: boolean;
	rate: number;
	setRate: (value: number) => void;
	termYears: number;
	setTermYears: (value: number) => void;
	termMin: number;
	termMax: number;
	collapsed: boolean;
	toggleCollapsed: () => void;
	monthlyPayment: number;
	totalPaid: number;
	overpay: number;
};

export const useLoanCalculator = (
	goal: GoalDetail,
): UseLoanCalculatorResult => {
	const isMortgage =
		GOAL_CATEGORY_CONFIG[goal.category].loan ===
		FuturePortfolioTypeEnum.Mortgage;
	const term = isMortgage ? MORTGAGE_TERM : LOAN_TERM;

	const [rate, setRate] = useState<number>(LOAN_RATE_DEFAULT);
	const [termYears, setTermYears] = useState<number>(term.termYears);
	const [collapsed, setCollapsed] = useState<boolean>(true);

	const { monthlyPayment, totalPaid, overpay } = computeLoanTotals(
		goal.loanAmount,
		rate,
		termYears,
	);

	return {
		isMortgage,
		rate,
		setRate,
		termYears,
		setTermYears,
		termMin: term.termMin,
		termMax: term.termMax,
		collapsed,
		toggleCollapsed: () => setCollapsed((c) => !c),
		monthlyPayment,
		totalPaid,
		overpay,
	};
};
